import classNames from 'classnames';
import React, { useState } from 'react';
import type { Accept, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import type { DistributedOmit } from 'type-fest';
import fileIcon from '../icons/non-prefilled/28x28/dropzoneFile.svg';
import imageIcon from '../icons/non-prefilled/28x28/dropzoneImage.svg';
import isFileOverlarge from '../utils/network/isFileOverlarge';
import LoadingIndicator from './LoadingIndicator';
import SVGIcon from './SVGIcon';
import * as styles from './Uploader.module.scss';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const UPLOADER_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  ACCEPT: 'accept',
  REJECT: 'reject',
} as const;

type UploaderStatus = (typeof UPLOADER_STATUS)[keyof typeof UPLOADER_STATUS];

type Type = 'image' | 'file';

const TYPE_TO_ICON = {
  image: imageIcon,
  file: fileIcon,
} satisfies Record<Type, React.ReactNode>;

const ICON_SIZE = 24;

const UploaderBorderContainer = ({
  children,
  status,
}: {
  children: React.ReactNode;
  status: UploaderStatus;
}) => {
  return (
    <div
      className={classNames(styles.uploaderBorderContainer, {
        [styles.uploaderAccept]: status === UPLOADER_STATUS.ACCEPT,
        [styles.uploaderReject]: status === UPLOADER_STATUS.REJECT,
      })}
    >
      <svg className={styles.uploaderBorder}>
        <rect />
      </svg>
      {children}
    </div>
  );
};

type BaseUploaderProps = {
  type: Type;
  status: UploaderStatus;
  info?: React.ReactNode;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseUploader = ({ type, status, info }: BaseUploaderProps) => {
  return (
    <UploaderBorderContainer status={status}>
      <div className={styles.uploader}>
        {status === UPLOADER_STATUS.UPLOADING ? (
          <div className={styles.uploaderIcon}>
            <LoadingIndicator delay={0} />
          </div>
        ) : (
          <SVGIcon size={ICON_SIZE} className={styles.uploaderIcon}>
            {TYPE_TO_ICON[type]}
          </SVGIcon>
        )}
        <div>
          {status === UPLOADER_STATUS.UPLOADING ? (
            'Uploading…'
          ) : (
            <>
              <div>
                Drag & drop{' '}
                <span className={styles.wrappingGroup}>or click to upload</span>
              </div>
              {!!info && <div className={styles.info}>{info}</div>}
            </>
          )}
        </div>
      </div>
    </UploaderBorderContainer>
  );
};

type UploaderProps = DistributedOmit<BaseUploaderProps, 'status'> & {
  accept?: Accept;
  multiple?: boolean;
  onDrop: (files: File[]) => void | Promise<void>;
  maxSize?: {
    limitMB: number;
    onMaxSizeError: (file: File) => void;
  };
  autoFocus?: boolean;
};

const Uploader = ({
  accept,
  multiple = false,
  onDrop,
  maxSize,
  autoFocus = true,
  ...props
}: UploaderProps) => {
  const [uploading, setUploading] = useState(false);

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    fileRejections.forEach(({ file }) => {
      if (maxSize && isFileOverlarge(file, maxSize.limitMB)) {
        maxSize.onMaxSizeError(file);
      }
    });

    if (acceptedFiles.length > 0) {
      setUploading(true);

      const promise = onDrop(acceptedFiles) || Promise.resolve();

      promise.then(() => {
        setUploading(false);
      });
    }
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      accept, // on windows we can get empty types easily, e.g. .md file
      multiple,
      maxSize: maxSize ? maxSize.limitMB * 1000 * 1000 : undefined,
      onDrop: handleDrop,
      autoFocus,
    });

  let status;
  if (uploading) {
    status = UPLOADER_STATUS.UPLOADING;
  } else if (isDragAccept) {
    status = UPLOADER_STATUS.ACCEPT;
  } else if (isDragReject) {
    status = UPLOADER_STATUS.REJECT;
  } else {
    status = UPLOADER_STATUS.IDLE;
  }

  return (
    <div {...getRootProps()} className={styles.container}>
      <input {...getInputProps()} />
      <BaseUploader status={status} {...props} />
    </div>
  );
};

export default Uploader;
